import React from 'react';
import { Eye, EyeOff, Loader2, Play } from 'lucide-react';

import { formatResizeDisplay, formatTokenCount, toFiniteCount } from '../../utils/formatters';
import { getGroupImageUrls, getGroupKey, normalizeHistoryImageKey } from './historyUtils';

const HistoryGroupHeader = ({
  group,
  currentUsername,
  disabledImageKeys,
  rerunningGroup,
  onOpenImageGroup,
  onToggleImage,
  onRerunGroup,
}) => {
  const gKey = getGroupKey(group);
  const allUrls = getGroupImageUrls(group);
  const groupDisabled = new Set(
    (Array.isArray(disabledImageKeys[gKey]) ? disabledImageKeys[gKey] : [])
      .map((key) => normalizeHistoryImageKey(key))
      .filter(Boolean),
  );
  const enabledKeySet = new Set(
    allUrls
      .map((url) => normalizeHistoryImageKey(url))
      .filter((key) => key && !groupDisabled.has(key)),
  );
  const enabledCount = enabledKeySet.size;
  const hasDisabled = groupDisabled.size > 0;
  const isRerunning = rerunningGroup === gKey;
  const representativeItem = group.items?.[0] || {};
  const usage = (group.usage && typeof group.usage === 'object')
    ? group.usage
    : ((representativeItem.usage && typeof representativeItem.usage === 'object') ? representativeItem.usage : {});
  const inputTokens = toFiniteCount(
    usage.prompt_tokens ?? usage.input_tokens ?? group.input_tokens_consumed ?? representativeItem.input_tokens_consumed ?? null,
  );
  const outputTokens = toFiniteCount(
    usage.completion_tokens ?? usage.output_tokens ?? group.output_tokens_consumed ?? representativeItem.output_tokens_consumed ?? null,
  );
  const rawTotalTokens = toFiniteCount(
    usage.total_tokens ?? group.total_tokens_consumed ?? representativeItem.total_tokens_consumed ?? null,
  );
  const totalTokens = (
    inputTokens === null && outputTokens === null
      ? null
      : (rawTotalTokens !== null
        ? rawTotalTokens
        : (inputTokens !== null && outputTokens !== null ? (inputTokens + outputTokens) : null))
  );
  const sourceRaw = String(group.analysis_source ?? representativeItem.analysis_source ?? '').trim().toLowerCase();
  const cacheHit = group.cache_hit === true || representativeItem.cache_hit === true || sourceRaw === 'cache';
  const sourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const sourceClass = cacheHit
    ? 'bg-gold/10 text-gold border border-gold/20'
    : 'bg-positive/10 text-positive border border-positive/20';
  const useEbayRag = (
    group.use_ebay_rag !== undefined && group.use_ebay_rag !== null
      ? Boolean(group.use_ebay_rag)
      : Boolean(representativeItem.use_ebay_rag)
  );
  const ragRefs = Math.max(0, Number(group.ebay_rag_result_count ?? representativeItem.ebay_rag_result_count ?? 0) || 0);
  const useNotesRag = (
    group.use_notes_rag !== undefined && group.use_notes_rag !== null
      ? Boolean(group.use_notes_rag)
      : Boolean(representativeItem.use_notes_rag)
  );
  const notesRefs = Math.max(0, Number(group.notes_rag_example_count ?? representativeItem.notes_rag_example_count ?? 0) || 0);
  const assumeHiddenItems = (
    group.assume_hidden_items !== undefined && group.assume_hidden_items !== null
      ? Boolean(group.assume_hidden_items)
      : Boolean(representativeItem.assume_hidden_items)
  );

  return (
    <div className="bg-surface-2 p-3 sm:p-4 border-b border-hairline">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <div className="flex gap-2 flex-wrap">
          {allUrls.map((url, idx) => {
            const imageKey = normalizeHistoryImageKey(url);
            const isDisabled = imageKey ? groupDisabled.has(imageKey) : false;
            const disablingLastEnabledImage = !isDisabled && enabledCount <= 1;
            return (
              <div key={idx} className="relative">
                <div
                  className="cursor-pointer"
                  onClick={() => onOpenImageGroup(url, allUrls, group.items)}
                >
                  <img
                    src={url}
                    alt={`Image ${idx + 1}`}
                    className={`w-14 h-14 sm:w-16 sm:h-16 object-cover rounded border-2 shadow-sm transition-all ${
                      isDisabled ? 'opacity-30 grayscale border-danger' : 'border-hairline hover:scale-105'
                    }`}
                  />
                </div>
                {allUrls.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleImage(gKey, url, allUrls);
                    }}
                    disabled={disablingLastEnabledImage}
                    className={`absolute -top-1.5 -right-1.5 p-0.5 rounded-full border shadow-sm transition-colors z-10 ${
                      disablingLastEnabledImage
                        ? 'bg-surface-2 border-hairline text-muted cursor-not-allowed'
                        : (isDisabled
                          ? 'bg-danger/10 border-danger/30 text-danger hover:bg-danger/20'
                          : 'bg-positive/10 border-positive/30 text-positive hover:bg-positive/20')
                    }`}
                    title={disablingLastEnabledImage ? 'At least one image must stay enabled' : (isDisabled ? 'Enable image' : 'Disable image')}
                  >
                    {isDisabled ? <EyeOff size={10} /> : <Eye size={10} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold text-ink">{new Date(group.timestamp).toLocaleString()}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-[10px] font-mono text-muted bg-surface-2 border border-hairline px-2 py-0.5 rounded">{group.model_name}</span>
            {group.username && (group.user_deleted || group.username !== currentUsername) && (
              <span
                className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                  group.user_deleted
                    ? 'text-danger bg-danger/10 border-danger/20'
                    : 'text-muted bg-surface-2 border-hairline'
                }`}
                title={group.user_deleted
                  ? `Analyzed by ${group.username} (account deleted)`
                  : `Analyzed by ${group.username}`}
              >
                By: {group.username}{group.user_deleted ? ' (deleted)' : ''}
              </span>
            )}
            <span className="text-[10px] font-mono text-positive bg-positive/10 border border-positive/20 px-2 py-0.5 rounded">
              Resize: {formatResizeDisplay(group.resize_mode)}
            </span>
            <span className="text-[10px] font-mono text-warn bg-warn/10 border border-warn/20 px-2 py-0.5 rounded">
              Compression: {group.compression_mode || 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-teal bg-teal/10 border border-teal/20 px-2 py-0.5 rounded">
              eBay RAG: {useEbayRag ? `on (${ragRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-ai bg-ai/10 border border-ai/20 px-2 py-0.5 rounded">
              Notes RAG: {useNotesRag ? `on (${notesRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-ai bg-ai/10 border border-ai/20 px-2 py-0.5 rounded">
              Hidden Items: {assumeHiddenItems ? 'on' : 'off'}
            </span>
            <span className="text-[10px] font-mono text-teal bg-teal/10 border border-teal/20 px-2 py-0.5 rounded">
              {group.analysis_duration_ms !== undefined && group.analysis_duration_ms !== null
                ? `${Number(group.analysis_duration_ms).toFixed(0)}ms`
                : 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-ai bg-ai/10 border border-ai/20 px-2 py-0.5 rounded">
              {group.analysis_cost_display !== undefined
                ? group.analysis_cost_display
                : (group.analysis_cost !== undefined && group.analysis_cost !== null
                  ? `$${Number(group.analysis_cost).toFixed(4)}`
                  : 'N/A')}
            </span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${sourceClass}`}>
              Src: {sourceLabel}
            </span>
            <span className="text-[10px] font-mono text-teal bg-teal/10 border border-teal/20 px-2 py-0.5 rounded">
              In Tok: {formatTokenCount(inputTokens)}
            </span>
            <span className="text-[10px] font-mono text-ai bg-ai/10 border border-ai/20 px-2 py-0.5 rounded">
              Out Tok: {formatTokenCount(outputTokens)}
            </span>
            <span className="text-[10px] font-mono text-positive bg-positive/10 border border-positive/20 px-2 py-0.5 rounded">
              Tot Tok: {formatTokenCount(totalTokens)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {hasDisabled && (
            <button
              onClick={() => onRerunGroup(group)}
              disabled={isRerunning || enabledCount === 0}
              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border transition-colors ${
                isRerunning || enabledCount === 0
                  ? 'bg-surface-2 text-muted border-hairline cursor-not-allowed'
                  : 'bg-accent/10 text-accent border-accent/30 hover:bg-accent/20'
              }`}
              title={`Re-run with ${enabledCount}/${allUrls.length} images`}
            >
              {isRerunning ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Re-run ({enabledCount}/{allUrls.length})
            </button>
          )}
          <div className="text-right">
            <div className="text-xs text-muted uppercase tracking-wider font-bold">Items Found</div>
            <div className="text-2xl font-bold text-ink">{group.items.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryGroupHeader;
